import { useContext } from 'react'
import { useSelector } from 'react-redux'

import { digObject, sortArrayBy, toggleArray } from '@domain-group/javascript-utils'

import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import PageContext from '@contexts/pageContext'

import * as serviceProductActions from '@redux/modules/serviceProduct'

import defaultState, { requiredFields } from '@models/serviceProduct'

export const generateUrls = (serviceProduct = {}) => {
  const { id } = serviceProduct

  return {
    serviceProductAuditLogsUrl: `#/systemManager/products/${id}/edit/auditLog`,
    editServiceProductRelationsUrl: `#/systemManager/products/${id}/edit/relations`,
    editServiceProductUrl: `#/systemManager/products/${id}/edit`,
    serviceProductsIndexUrl: '#/systemManager/products',
  }
}

const addCompatibleProduct = params => new Promise((resolve, reject) => {
  const { serviceProduct, showCreateCompatibleProductModal } = params
  if (showCreateCompatibleProductModal){
    const payload = {
      serviceProduct,
    }

    showCreateCompatibleProductModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateCompatibleProductModal not defined in PageContext callbacks'))
})

const manageServiceProductPublications = params => new Promise((resolve, reject) => {
  const {
    serviceProduct, showManageServiceProductPublicationsModal, customPayload = {},
  } = params

  if (showManageServiceProductPublicationsModal){
    const payload = {
      serviceProduct,
      ...customPayload,
    }

    showManageServiceProductPublicationsModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showManageServiceProductPublicationsModal not defined in PageContext callbacks'))
})

const createServiceProduct = (params) => {
  const { serviceProductParams, dispatch, requestOptions } = params
  const { createServiceProduct: createFn } = serviceProductActions

  const stringifyObjectKeys = ['options']
  const updatedParams = mergeAndStringifyEntityObjects({}, serviceProductParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editServiceProductUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editServiceProductUrl,
      }
    }

    return response
  })
}

const updateServiceProduct = (params) => {
  const {
    serviceProduct, serviceProductParams, dispatch, requestOptions,
  } = params
  const { updateServiceProduct: updateFn } = serviceProductActions

  const stringifyObjectKeys = ['options']
  const updatedParams = mergeAndStringifyEntityObjects(serviceProduct, serviceProductParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

const toggleCompatibleProduct = (params) => {
  const {
    compatibleProductId, serviceProduct, dispatch, requestOptions,
  } = params

  const { options } = serviceProduct
  const { compatible_products } = options || {}

  const updatedCompatibleProducts = toggleArray(compatible_products, compatibleProductId)

  const serviceProductParams = {
    options: { compatible_products: updatedCompatibleProducts },
  }

  const toggleRequestOptions = {
    ...requestOptions,
    include_service_product_compatible_products: true,
  }

  return updateServiceProduct({
    serviceProduct, serviceProductParams, dispatch, requestOptions: toggleRequestOptions,
  })
}

const togglePublication = (params) => {
  const {
    publicationId, optionsKey, serviceProduct, dispatch, requestOptions,
  } = params
  const compatiblePublications = digObject(serviceProduct, `options.${optionsKey}.compatible_publications`)

  const updatedCompatiblePublications = toggleArray(compatiblePublications, publicationId)

  const serviceProductParams = {
    options: {
      [optionsKey]: {
        compatible_publications: updatedCompatiblePublications,
      },
    },
  }

  const toggleRequestOptions = {
    ...requestOptions,
    include_service_product_assigned_publications: true,
  }

  return updateServiceProduct({
    serviceProduct, serviceProductParams, dispatch, requestOptions: toggleRequestOptions,
  })
}

const toggleCategory = (params) => {
  const {
    categoryId, serviceProduct, dispatch, requestOptions,
  } = params

  const categories = digObject(serviceProduct, 'options.categories', [])

  const updatedCategories = toggleArray(categories, categoryId)

  const serviceProductParams = {
    options: {
      categories: updatedCategories,
    },
  }

  return updateServiceProduct({
    serviceProduct, serviceProductParams, dispatch, requestOptions,
  })
}

const duplicateServiceProduct = (params) => {
  const {
    serviceProduct, serviceProductParams, dispatch, requestOptions,
  } = params
  const { duplicateServiceProduct: duplicateFn } = serviceProductActions

  const updatedParams = {
    id: serviceProduct.id,
    ...serviceProductParams,
  }

  return dispatch(duplicateFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { id } = data

      const { editServiceProductUrl } = generateUrls({ id })

      return {
        data,
        redirectUrl: editServiceProductUrl,
        success,
      }
    }

    return response
  })
}

const addTemplatePage = params => new Promise((resolve, reject) => {
  const { serviceProduct, showCreateServiceProductTemplatePageModal } = params

  if (showCreateServiceProductTemplatePageModal){
    const payload = {
      serviceProduct,
    }

    showCreateServiceProductTemplatePageModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateServiceProductTemplatePageModal not defined in PageContext callbacks'))
})

const launchDuplicateServiceProductModal = params => new Promise((resolve, reject) => {
  const { serviceProduct, showDuplicateServiceProductModal } = params

  if (showDuplicateServiceProductModal){
    const payload = {
      serviceProduct,
    }

    showDuplicateServiceProductModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showDuplicateServiceProductModal not defined in PageContext callbacks'))
})

const manageServiceProductTaskOptions = params => new Promise((resolve, reject) => {
  const { serviceProduct, showEditProductTaskSettingsModal } = params

  if (showEditProductTaskSettingsModal){
    const payload = {
      serviceProduct,
    }

    showEditProductTaskSettingsModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showEditProductTaskSettingsModal not defined in PageContext callbacks'))
})

export function useServiceProductForm(serviceProduct = {}, options = {}){
  const { validateOn } = options

  const serviceProductForm = useForm(
    defaultState,
    { entity: serviceProduct, requiredFields, validateOn },
    [serviceProduct.id, serviceProduct.cache_key],
  )

  return {
    ...serviceProductForm,
  }
}

export function useRelations(serviceProduct){
  const { service_id } = serviceProduct || {}

  const services = useSelector(reduxState => reduxState.entities.services)

  const service = services[service_id] || {}

  return {
    service,
  }
}

function useServiceProduct(initEntity = {}){
  const { entity: serviceProduct } = useLatestEntity(initEntity, 'serviceProducts')
  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const {
    showCreateCompatibleProductModal,
    showCreateServiceProductTemplatePageModal,
    showDuplicateServiceProductModal,
    showEditProductTaskSettingsModal,
    showManageServiceProductPublicationsModal,
  } = callbacks || {}

  const { creating, loading, updating } = useSelector(reduxState => reduxState.serviceProducts)
  const serviceProducts = useSelector(reduxState => reduxState.entities.serviceProducts)

  const { service } = useRelations(serviceProduct)

  // Sort compatible products by title
  const compatibleProductIds = digObject(serviceProduct, 'options.compatible_products', [])
  const sortedCompatibleProductIds = sortArrayBy(compatibleProductIds, 'asc', id => serviceProducts[id]?.title)

  return {
    callbacks: {
      addCompatibleProduct: () => addCompatibleProduct({ serviceProduct, showCreateCompatibleProductModal }),
      addTemplatePage: () => addTemplatePage({ serviceProduct, showCreateServiceProductTemplatePageModal }),
      createServiceProduct: (serviceProductParams, requestOptions) => (
        createServiceProduct({ serviceProductParams, dispatch, requestOptions })
      ),
      duplicateServiceProduct: (serviceProductParams, requestOptions) => duplicateServiceProduct({
        serviceProduct, serviceProductParams, dispatch, requestOptions,
      }),
      launchDuplicateServiceProductModal: () => (
        launchDuplicateServiceProductModal({ serviceProduct, showDuplicateServiceProductModal })
      ),
      manageServiceProductPublications: customPayload => manageServiceProductPublications({
        serviceProduct, showManageServiceProductPublicationsModal, customPayload,
      }),
      manageServiceProductTaskOptions: () => (
        manageServiceProductTaskOptions({ serviceProduct, showEditProductTaskSettingsModal })
      ),
      toggleCategory: (categoryId, requestOptions) => toggleCategory({
        categoryId, serviceProduct, dispatch, requestOptions,
      }),
      toggleCompatibleProduct: (compatibleProductId, requestOptions) => toggleCompatibleProduct({
        compatibleProductId, serviceProduct, dispatch, requestOptions,
      }),
      togglePublication: (publicationId, optionsKey, requestOptions) => togglePublication({
        publicationId, optionsKey, serviceProduct, dispatch, requestOptions,
      }),
      updateServiceProduct: (serviceProductParams, requestOptions) => updateServiceProduct({
        serviceProduct, serviceProductParams, dispatch, requestOptions,
      }),
    },
    compatibleProductIds: sortedCompatibleProductIds,
    creating,
    loading,
    service,
    serviceProduct,
    updating,
    urls: generateUrls(serviceProduct),
  }
}

export default useServiceProduct
